import type { StatusMeta } from '@/components/status-tone';
import type { T3ActivityFile, T3ActivityItem, T3ActivityResult } from '@shared/agent-protocol';

/** The agent-activity feed's pure parts (T-27): merging polls, where the next poll starts, and labels. */

/** The run step whose row opens the feed. */
export const AGENT_WAIT_STEP_TYPE = 'agent.t3.wait';

/** Item statuses that can still change (text growing, a command still running). */
const LIVE_ITEM_STATUSES = new Set(['in_progress', 'inProgress', 'running', 'pending', 'started', 'streaming']);

export const isLiveStatus = (status: string | null) => status != null && LIVE_ITEM_STATUSES.has(status);

export const isLiveItem = (item: T3ActivityItem) => isLiveStatus(item.status);

/** Items by position, oldest first; a newer read of a position replaces the older one. */
export function mergeItems(current: readonly T3ActivityItem[], next: readonly T3ActivityItem[]): T3ActivityItem[] {
  const byPosition = new Map<number, T3ActivityItem>();
  for (const item of current) byPosition.set(item.position, item);
  for (const item of next) byPosition.set(item.position, item);
  return [...byPosition.values()].sort((a, b) => a.position - b.position);
}

/**
 * Where the next poll reads from (`after`): before the first item still in progress, so its status and
 * text catch up, else after the last item. Null when there's nothing yet (read the latest).
 */
export function nextAfter(items: readonly T3ActivityItem[]): number | null {
  if (!items.length) return null;
  const live = items.find(isLiveItem);
  if (live) return live.position > 0 ? live.position - 1 : null;
  return items[items.length - 1]!.position;
}

/** A newer read folded into what's shown: its thread and subagents, the items merged. */
export function mergeActivity(current: T3ActivityResult | undefined, next: T3ActivityResult, mode: 'newer' | 'earlier'): T3ActivityResult {
  if (!current) return next;
  return {
    thread: mode === 'newer' ? next.thread : current.thread,
    items: mergeItems(current.items, next.items),
    hasEarlier: mode === 'earlier' ? next.hasEarlier : current.hasEarlier,
    subagents: mode === 'newer' ? next.subagents : current.subagents,
  };
}

const ITEM_LABELS: Record<string, string> = {
  reasoning: 'Thinking',
  command_execution: 'Command',
  dynamic_tool: 'Tool',
  mcp_tool_call: 'Tool',
  tool_call: 'Tool',
  assistant_message: 'Agent',
  user_message: 'Prompt',
  file_change: 'File change',
  checkpoint: 'Changed files',
  web_search: 'Web search',
  plan: 'Plan',
  proposed_plan: 'Plan',
  todo_list: 'To-do list',
  subagent: 'Subagent',
  error: 'Error',
};

/** A short label for T3's item type: reasoning → Thinking, command_execution → Command, … */
export function itemLabel(type: string): string {
  return ITEM_LABELS[type] ?? type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** Shown in a monospace block: commands, tool calls and file lists. */
export const isCodeItem = (type: string) => ['command_execution', 'dynamic_tool', 'mcp_tool_call', 'tool_call', 'file_change', 'checkpoint'].includes(type);

/** A tool call's text is JSON with its name first: "Tool · Read" reads better than the JSON. */
export function toolName(item: T3ActivityItem): string | null {
  if (!item.text || !['dynamic_tool', 'mcp_tool_call', 'tool_call'].includes(item.type)) return null;
  return /"toolName"\s*:\s*"([^"]+)"/.exec(item.text)?.[1] ?? null;
}

/** T3 thread and item statuses, for StatusBadge. Unknown ones show as they are, in neutral. */
export function activityStatusMeta(status: string | null): StatusMeta | null {
  if (!status) return null;
  if (LIVE_ITEM_STATUSES.has(status)) return { label: 'Running', tone: 'info' };
  switch (status) {
    case 'starting':
    case 'preparing':
      return { label: 'Starting', tone: 'info' };
    case 'queued':
      return { label: 'Queued', tone: 'neutral' };
    case 'waiting':
      return { label: 'Waiting', tone: 'warning' };
    case 'completed':
    case 'idle':
      return { label: 'Done', tone: 'success' };
    case 'failed':
      return { label: 'Failed', tone: 'danger' };
    case 'declined':
      return { label: 'Declined', tone: 'warning' };
    default:
      return { label: itemLabel(status), tone: 'neutral' };
  }
}

/** A file's kind beside its path; null for a plain edit (modified), which needs no word. */
export function fileKindLabel(kind: string): string | null {
  switch (kind) {
    case 'modified':
    case 'updated':
      return null;
    case 'created':
    case 'added':
      return 'new';
    case 'failed':
      return "didn't apply";
    default:
      return kind;
  }
}

/** SF-T-222: items that list files (file_change, checkpoint): their counts go in the changed-files widget, not the thread. */
export const isFileItem = (item: T3ActivityItem) => item.type === 'file_change' || item.type === 'checkpoint';

/** SF-T-222: shown as one line with a copy button (commands and tool calls), the whole text on hover. */
export const isOneLineItem = (type: string) => ['command_execution', 'dynamic_tool', 'mcp_tool_call', 'tool_call'].includes(type);

/** A command's text without the "$ " T3 puts before it: what the copy button copies. */
export const commandText = (text: string) => text.replace(/^\$ /, '');

export interface ChangedFiles {
  files: T3ActivityFile[];
  additions: number;
  deletions: number;
}

/**
 * SF-T-222: the files the items changed, each once with its lines summed, and the totals. Checkpoints (each turn's
 * changed files) are the record up to the last one; after it, the file_change items of the turn still going (all of
 * them for an agent whose turns don't checkpoint). A file's kind is the newest one seen, except that a file made in the run stays new.
 */
export function changedFiles(items: readonly T3ActivityItem[]): ChangedFiles {
  const checkpoints = items.filter((i) => i.type === 'checkpoint' && i.files?.length);
  // The turn still going (after the last checkpoint) has only its file changes so far.
  const lastCheckpoint = checkpoints.at(-1)?.position ?? -1;
  const source = [...checkpoints, ...items.filter((i) => i.type === 'file_change' && i.files?.length && i.position > lastCheckpoint)];
  const byPath = new Map<string, T3ActivityFile>();
  for (const item of source) {
    for (const f of item.files ?? []) {
      const was = byPath.get(f.path);
      if (!was) {
        byPath.set(f.path, { ...f });
        continue;
      }
      const sum = (a: number | null, b: number | null) => (a == null && b == null ? null : (a ?? 0) + (b ?? 0));
      const created = was.kind === 'created' || was.kind === 'added';
      byPath.set(f.path, {
        path: f.path,
        kind: created && f.kind !== 'deleted' ? was.kind : f.kind,
        additions: sum(was.additions, f.additions),
        deletions: sum(was.deletions, f.deletions),
      });
    }
  }
  const files = [...byPath.values()].sort((a, b) => a.path.localeCompare(b.path));
  return {
    files,
    additions: files.reduce((n, f) => n + (f.additions ?? 0), 0),
    deletions: files.reduce((n, f) => n + (f.deletions ?? 0), 0),
  };
}

export interface FileTreeNode {
  /** The folder's path from its parent ("src/components" when a folder holds only one folder), or the file's name. */
  name: string;
  /** Files only. */
  file?: T3ActivityFile;
  children: FileTreeNode[];
}

/** SF-T-222: the changed files as a tree, folders first, a folder holding only one folder merged into it ("src/lib"). */
export function fileTree(files: readonly T3ActivityFile[]): FileTreeNode[] {
  const root: FileTreeNode = { name: '', children: [] };
  for (const f of files) {
    const parts = f.path.split('/').filter(Boolean);
    let node = root;
    parts.forEach((part, i) => {
      if (i === parts.length - 1) {
        node.children.push({ name: part, file: f, children: [] });
        return;
      }
      let next = node.children.find((c) => !c.file && c.name === part);
      if (!next) {
        next = { name: part, children: [] };
        node.children.push(next);
      }
      node = next;
    });
  }
  const tidy = (node: FileTreeNode): FileTreeNode => {
    let n = node;
    while (!n.file && n.children.length === 1 && !n.children[0]!.file) {
      const only = n.children[0]!;
      n = { name: `${n.name}/${only.name}`, children: only.children };
    }
    const children = n.children.map(tidy).sort((a, b) => Number(!!a.file) - Number(!!b.file) || a.name.localeCompare(b.name));
    return { ...n, children };
  };
  return root.children.map(tidy).sort((a, b) => Number(!!a.file) - Number(!!b.file) || a.name.localeCompare(b.name));
}

/**
 * SF-T-222: a tool call's one line: its input's values ("frontend/src/a.ts" for a Read) rather than the JSON T3 gives.
 * The text as it is when it isn't that JSON (or was cut short).
 */
export function toolLine(text: string): string {
  try {
    const parsed = JSON.parse(text) as { input?: unknown };
    const input = parsed?.input;
    if (input && typeof input === 'object' && !Array.isArray(input)) {
      const values = Object.values(input as Record<string, unknown>).map((v) => (typeof v === 'string' ? v : JSON.stringify(v)));
      if (values.length) return values.join(' · ');
    }
  } catch {
    // Not JSON, or cut short by the machine.
  }
  return text;
}

// ---- SF-T-266: the thread folded the way t3code folds it ----

/** Over this many lines or characters, your message folds to a few lines (t3code's 8 and 600). */
const FOLD_LINES = 8;
const FOLD_CHARS = 600;

export const isLongMessage = (text: string) => text.length > FOLD_CHARS || text.split('\n').length > FOLD_LINES;

/** How long something has been going, to the second: "22s", "1m 12s", "8m", "1h 4m". */
export function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return seconds % 60 ? `${minutes}m ${seconds % 60}s` : `${minutes}m`;
  return minutes % 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${Math.floor(minutes / 60)}h`;
}

/** SF-T-266: the agent's work between messages (thinking, commands, tool calls, subagents), folded together in the thread. */
export const isWorkItem = (item: T3ActivityItem) => !['user_message', 'assistant_message', 'error', 'plan', 'proposed_plan', 'subagent'].includes(item.type);

/**
 * SF-T-266: the subagent each of a thread's `subagent` items started, by item position: exactly, when the item names
 * its thread (`subagentThreadId`, once T3 Code gives it). Otherwise T3's item names its subagent only by title and a
 * node id the thread list doesn't show, so items and subagent threads of the same title pair up in the order they were
 * started (the first item with the first thread made): two subagents with one title each open their
 * own. An item with no thread yet (the list is a little behind) has none.
 */
export function pairSubagents<T extends { threadId: string; title: string; createdAt: string | null }>(items: readonly T3ActivityItem[], subagents: readonly T[]): Map<number, T> {
  const out = new Map<number, T>();
  // An item that names its thread (T3 Code, once it says) pairs with it exactly; the rest by title and order.
  const exact = new Set<string>();
  for (const item of items) {
    const sub = item.type === 'subagent' && item.subagentThreadId ? subagents.find((s) => s.threadId === item.subagentThreadId) : undefined;
    if (sub) {
      out.set(item.position, sub);
      exact.add(sub.threadId);
    }
  }
  const byTitle = new Map<string, T[]>();
  for (const sub of [...subagents].filter((s) => !exact.has(s.threadId)).sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''))) {
    const title = sub.title.trim();
    byTitle.set(title, [...(byTitle.get(title) ?? []), sub]);
  }
  const used = new Map<string, number>();
  for (const item of [...items].filter((i) => i.type === 'subagent' && !out.has(i.position)).sort((a, b) => a.position - b.position)) {
    const title = item.title?.trim() ?? '';
    const i = used.get(title) ?? 0;
    const sub = byTitle.get(title)?.[i];
    used.set(title, i + 1);
    if (title && sub) out.set(item.position, sub);
  }
  return out;
}

/** A row of the thread: one item, or a run of the agent's work side by side, folded to one line. */
export type ThreadRow = { kind: 'item'; item: T3ActivityItem } | { kind: 'tools'; items: T3ActivityItem[] };

/**
 * The items as rows: two or more pieces of work in a row (thinking, commands, tool calls) fold into one `tools` row
 * ("Ran 3 commands and read 2 files"); a lone one stays its own line. With `live`, the run at the end (the one the
 * agent is in) stays open: it isn't folded until something follows it.
 */
export function threadRows(items: readonly T3ActivityItem[], live = false): ThreadRow[] {
  const rows: ThreadRow[] = [];
  let run: T3ActivityItem[] = [];
  const flush = (atEnd: boolean) => {
    if (run.length > 1 && !(atEnd && live)) rows.push({ kind: 'tools', items: run });
    else for (const item of run) rows.push({ kind: 'item', item });
    run = [];
  };
  for (const item of items) {
    if (isWorkItem(item)) {
      run.push(item);
      continue;
    }
    flush(false);
    rows.push({ kind: 'item', item });
  }
  flush(true);
  return rows;
}

/**
 * SF-T-266: one turn of the thread, as t3code folds it: the prompt it started with, the agent's answer (its last
 * message), and everything else in between (`work`, folded behind "Worked for 4m" once the turn is `done`).
 */
export interface ThreadTurn {
  /** The first item's position: the turn's key. */
  key: number;
  prompt: T3ActivityItem | null;
  /** In order, without the prompt, the answer and the messages steered in. */
  work: T3ActivityItem[];
  /** SF-T-266: messages steered into the turn while it went (shown, not folded away). */
  steers: T3ActivityItem[];
  answer: T3ActivityItem | null;
  /** All of the turn's items but the prompt, in order (the turn going now shows them as they come). */
  items: T3ActivityItem[];
  /** Over: any turn but the last, and the last once the thread isn't live. */
  done: boolean;
  /** How long it took, from its first item to its last (ms); null without times. */
  tookMs: number | null;
  /** SF-T-266: how a finished turn ended: stopped (interrupted or cancelled) and failed turns stay open. */
  ended: 'done' | 'stopped' | 'failed';
}

const STOPPED = new Set(['interrupted', 'cancelled']);

/**
 * SF-T-266: whether a prompt starts a new turn: a Steer goes into the T3 run that's going, so its message has the run
 * id of the turn it joins; a prompt in a run of its own (or with none said) starts one.
 */
function startsTurn(item: T3ActivityItem, current: readonly T3ActivityItem[] | undefined): boolean {
  if (item.type !== 'user_message') return false;
  return !(item.runId && current?.some((other) => other.runId === item.runId));
}

/** SF-T-266: where the turn going now starts in the items (its prompt; 0 when there's none). */
export function turnStartIndex(items: readonly T3ActivityItem[]): number {
  let start = 0;
  items.forEach((item, i) => {
    if (startsTurn(item, items.slice(start, i))) start = i;
  });
  return start;
}

/** The items (oldest first) split into turns at each prompt sent to the agent (a Steer stays in its turn). */
export function threadTurns(items: readonly T3ActivityItem[], live: boolean, threadStatus: string | null = null): ThreadTurn[] {
  const groups: T3ActivityItem[][] = [];
  for (const item of items) {
    if (!groups.length || startsTurn(item, groups.at(-1))) groups.push([item]);
    else groups.at(-1)!.push(item);
  }
  return groups.map((group, i) => {
    const prompt = group[0]!.type === 'user_message' ? group[0]! : null;
    const rest = prompt ? group.slice(1) : group;
    let answerIndex = -1;
    rest.forEach((item, j) => {
      if (item.type === 'assistant_message') answerIndex = j;
    });
    const answer = answerIndex >= 0 ? rest[answerIndex]! : null;
    const times = group.map((item) => (item.updatedAt ? Date.parse(item.updatedAt) : NaN)).filter((t) => !Number.isNaN(t));
    const last = i === groups.length - 1;
    const ended: ThreadTurn['ended'] =
      rest.some((item) => item.type === 'error') || (last && threadStatus === 'failed')
        ? 'failed'
        : rest.some((item) => STOPPED.has(item.status ?? '')) || (last && STOPPED.has(threadStatus ?? ''))
          ? 'stopped'
          : 'done';
    return {
      ended,
      key: group[0]!.position,
      prompt,
      work: rest.filter((item, j) => j !== answerIndex && item.type !== 'user_message'),
      steers: rest.filter((item) => item.type === 'user_message'),
      answer,
      items: rest,
      done: i < groups.length - 1 || !live,
      tookMs: times.length > 1 ? Math.max(...times) - Math.min(...times) : null,
    };
  });
}

/** What a tool call does, by its tool's name, for the folded line. */
type ToolAction = 'command' | 'read' | 'edit' | 'search' | 'web' | 'subagent' | 'tool';

const TOOL_ACTIONS: Record<string, ToolAction> = {
  read: 'read',
  edit: 'edit',
  write: 'edit',
  multiedit: 'edit',
  notebookedit: 'edit',
  grep: 'search',
  glob: 'search',
  websearch: 'web',
  webfetch: 'web',
  bash: 'command',
};

function toolAction(item: T3ActivityItem): ToolAction {
  if (item.type === 'command_execution') return 'command';
  if (item.type === 'subagent') return 'subagent';
  if (item.type === 'web_search') return 'web';
  const name = toolName(item)?.toLowerCase().replace(/[^a-z]/g, '') ?? '';
  return TOOL_ACTIONS[name] ?? 'tool';
}

const ACTION_WORDS: Record<ToolAction, (n: number) => string> = {
  command: (n) => (n === 1 ? 'ran a command' : `ran ${n} commands`),
  read: (n) => (n === 1 ? 'read a file' : `read ${n} files`),
  edit: (n) => (n === 1 ? 'edited a file' : `made ${n} edits`),
  search: (n) => (n === 1 ? 'searched the code' : `searched the code ${n} times`),
  web: (n) => (n === 1 ? 'searched the web' : `searched the web ${n} times`),
  subagent: (n) => (n === 1 ? 'ran a subagent' : `ran ${n} subagents`),
  tool: (n) => (n === 1 ? 'used a tool' : `used ${n} tools`),
};

/**
 * A folded run's line, as t3code words it: the two things done most, then the rest as "N other actions" (thinking
 * counts only when there's nothing else).
 * "Ran 3 commands and read 2 files", "Read 4 files, ran a command and 2 other actions".
 */
export function toolRunSummary(items: readonly T3ActivityItem[]): string {
  const counts = new Map<ToolAction, number>();
  // Thinking only names the run when it's all there is.
  const thoughts = items.filter((item) => item.type === 'reasoning').length;
  if (thoughts === items.length) return thoughts === 1 ? 'Thought' : `Thought ${thoughts} times`;
  for (const item of items) {
    if (item.type === 'reasoning') continue;
    const action = toolAction(item);
    counts.set(action, (counts.get(action) ?? 0) + 1);
  }
  // Most first; a tie keeps the order they first came in.
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  const parts = ranked.slice(0, 2).map(([action, n]) => ACTION_WORDS[action](n));
  const rest = ranked.slice(2).reduce((sum, [, n]) => sum + n, 0);
  if (rest) parts.push(rest === 1 ? 'one other action' : `${rest} other actions`);
  const text = parts.length > 2 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : parts.join(' and ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** SF-T-266: what a folded run mostly is, for its icon (t3code's toolGroupSummaryKind): one kind of action, thinking, or mixed. */
export type ToolRunKind = 'thinking' | 'mixed' | ToolAction;

export function toolRunKind(items: readonly T3ActivityItem[]): ToolRunKind {
  const work = items.filter((item) => item.type !== 'reasoning');
  if (!work.length) return 'thinking';
  const kinds = new Set(work.map(toolAction));
  return kinds.size === 1 ? [...kinds][0]! : 'mixed';
}

/** SF-T-266: a command or tool call that failed (its icon goes red, as t3code's do). */
export const isFailedItem = (item: T3ActivityItem) => item.status === 'failed' || item.status === 'error' || item.status === 'declined';

/**
 * SF-T-266: a folded line's classes (a turn's "Worked for", a run of work, a thought, a command), as t3code's are: the
 * whole row is the button, its chevron (FoldChevron) at the right edge, and taller on touch screens.
 */
export const FOLD_LINE =
  'flex min-h-6 w-full min-w-0 items-center gap-1.5 rounded-sm text-left text-xs text-muted-foreground hover:text-foreground pointer-coarse:min-h-9 max-sm:text-sm';

/** SF-T-266: a message's text in the agent chats: larger on phones, as T3 Code's app (about 17pt there). */
export const MESSAGE_TEXT = 'text-sm max-sm:text-base';

/** SF-T-266: the turn the agent is in, for the Working pill: when it started, and its subagents at work. */
export interface ThreadWorking {
  since: string | null;
  agents: { running: number; total: number };
}

/** The turn going now in a live thread (after its last prompt), or null when the agent isn't working. */
export function threadWorking(feed: T3ActivityResult, live: boolean): ThreadWorking | null {
  if (!live || !isLiveStatus(feed.thread.status)) return null;
  const items = feed.items;
  const start = turnStartIndex(items);
  return {
    since: items[start]?.updatedAt ?? null,
    agents: { running: feed.subagents.filter((s) => isLiveStatus(s.status)).length, total: feed.subagents.length },
  };
}

/**
 * SF-T-266: the agent's latest thought in the turn going now, as T3 Code shows it over its live step: the thought's
 * bold heading when it has one, else its first sentence. Null when it hasn't thought yet this turn.
 */
export function latestThought(items: readonly T3ActivityItem[]): string | null {
  const start = turnStartIndex(items);
  for (let i = items.length - 1; i >= start; i--) {
    const item = items[i]!;
    if (item.type !== 'reasoning' || !item.text?.trim()) continue;
    const text = item.text.trim();
    const heading = /^\*\*(.+?)\*\*/.exec(text)?.[1] ?? /^#+\s*(.+)$/m.exec(text)?.[1];
    if (heading) return heading.trim();
    const first = text.split('\n').find((l) => l.trim())!.trim();
    return /^(.+?[.!?])(\s|$)/.exec(first)?.[1] ?? first;
  }
  return null;
}
